import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import {
  mockCustomers,
  mockInternalAccounts,
  mockPlatformInternalAccounts,
  mockExternalAccounts,
  mockPlatformExternalAccounts,
  mockTransactions,
  DEMO_EXCHANGE_RATES,
  CURRENCIES,
} from "../data/mock";
import type { InternalAccount, ExternalAccount, Transaction, Quote } from "../types";

interface DemoState {
  enabled: boolean;
  internalAccounts: InternalAccount[];
  platformInternalAccounts: InternalAccount[];
  externalAccounts: ExternalAccount[];
  platformExternalAccounts: ExternalAccount[];
  transactions: Transaction[];
  quotes: Quote[];
}

interface DemoContextType {
  demo: DemoState;
  toggleDemo: () => void;

  // --- Quote flow (POST /quotes → POST /quotes/:id/execute) ---
  /** Simulate POST /quotes - create a quote from internal account to external account or UMA */
  demoCreateQuote: (params: {
    sourceAccountId: string;
    destinationAccountId?: string;
    destinationUmaAddress?: string;
    destinationCurrency: string;
    amount: number;
    lockedSide: "SENDING" | "RECEIVING";
    description?: string;
  }) => { success: boolean; quote?: Quote; error?: string };

  /** Simulate POST /quotes/:id/execute - execute a pending quote */
  demoExecuteQuote: (quoteId: string) => { success: boolean; transaction?: Transaction; error?: string };

  // --- Same-currency transfers (POST /transfer-out, POST /transfer-in) ---
  /** Simulate POST /transfer-out - internal account → external account (same currency) */
  demoTransferOut: (params: {
    sourceInternalAccountId: string;
    destExternalAccountId: string;
    amount: number;
    description?: string;
  }) => { success: boolean; transaction?: Transaction; error?: string };

  /** Simulate POST /transfer-in - external account → internal account (same currency) */
  demoTransferIn: (params: {
    sourceExternalAccountId: string;
    destInternalAccountId: string;
    amount: number;
    description?: string;
  }) => { success: boolean; transaction?: Transaction; error?: string };

  // --- Account creation ---
  demoCreateExternalAccount: (params: {
    customerId: string;
    currency: string;
    accountType: string;
    accountLabel: string;
  }) => { success: boolean; account?: ExternalAccount; error?: string };

  demoCreateInternalAccount: (params: {
    customerId: string;
    currencyCode: string;
  }) => { success: boolean; account?: InternalAccount; error?: string };

  // --- Data accessors ---
  getDemoInternalAccounts: (customerId?: string) => InternalAccount[];
  getDemoExternalAccounts: (customerId?: string) => any[];
  getDemoCustomers: () => typeof mockCustomers;
  getDemoTransactions: (customerId?: string) => Transaction[];
  getDemoQuotes: () => Quote[];
}

const DemoContext = createContext<DemoContextType | null>(null);

export function DemoProvider({ children }: { children: ReactNode }) {
  const [demo, setDemo] = useState<DemoState>(() => ({
    enabled: sessionStorage.getItem("demo-mode") === "true",
    internalAccounts: [...mockInternalAccounts],
    platformInternalAccounts: [...mockPlatformInternalAccounts],
    externalAccounts: [...mockExternalAccounts],
    platformExternalAccounts: [...mockPlatformExternalAccounts],
    transactions: [...mockTransactions],
    quotes: [],
  }));

  const toggleDemo = useCallback(() => {
    setDemo((prev) => {
      const next = !prev.enabled;
      sessionStorage.setItem("demo-mode", String(next));
      return { ...prev, enabled: next };
    });
  }, []);

  const getDemoCustomers = useCallback(() => mockCustomers, []);

  const getDemoInternalAccounts = useCallback(
    (customerId?: string) => {
      const all = [...demo.internalAccounts, ...demo.platformInternalAccounts];
      if (!customerId) return all;
      if (customerId === "PLATFORM") return demo.platformInternalAccounts;
      return demo.internalAccounts.filter((a) => a.customerId === customerId);
    },
    [demo.internalAccounts, demo.platformInternalAccounts],
  );

  const getDemoExternalAccounts = useCallback(
    (customerId?: string) => {
      const all = [...demo.externalAccounts, ...demo.platformExternalAccounts];
      if (!customerId) return all;
      if (customerId === "PLATFORM") return demo.platformExternalAccounts;
      return demo.externalAccounts.filter((a) => a.customerId === customerId);
    },
    [demo.externalAccounts, demo.platformExternalAccounts],
  );

  const getDemoTransactions = useCallback(
    (customerId?: string) => {
      if (!customerId) return demo.transactions;
      return demo.transactions.filter((t) => t.customerId === customerId);
    },
    [demo.transactions],
  );

  const getDemoQuotes = useCallback(() => demo.quotes, [demo.quotes]);

  // Helper: find an internal account across customer + platform arrays
  const findInternalAccount = useCallback(
    (accountId: string) => {
      return [...demo.internalAccounts, ...demo.platformInternalAccounts].find((a) => a.id === accountId);
    },
    [demo.internalAccounts, demo.platformInternalAccounts],
  );

  // Helper: find an external account across customer + platform arrays
  const findExternalAccount = useCallback(
    (accountId: string) => {
      return [...demo.externalAccounts, ...demo.platformExternalAccounts].find((a) => a.id === accountId);
    },
    [demo.externalAccounts, demo.platformExternalAccounts],
  );

  // ─── POST /quotes simulation ────────────────────────────────
  const demoCreateQuote = useCallback(
    (params: {
      sourceAccountId: string;
      destinationAccountId?: string;
      destinationUmaAddress?: string;
      destinationCurrency: string;
      amount: number;
      lockedSide: "SENDING" | "RECEIVING";
      description?: string;
    }) => {
      const sourceAcct = findInternalAccount(params.sourceAccountId);
      if (!sourceAcct) return { success: false, error: "Source internal account not found" };

      const srcCurrency = sourceAcct.balance.currency.code;
      const destCurrency = params.destinationCurrency;
      const srcDecimals = sourceAcct.balance.currency.decimals;
      const destCurrObj = CURRENCIES[destCurrency] || { code: destCurrency, name: destCurrency, symbol: destCurrency, decimals: 2 };
      const destDecimals = destCurrObj.decimals;

      // Determine exchange rate
      let rate = 1.0;
      if (srcCurrency !== destCurrency) {
        const key = `${srcCurrency}:${destCurrency}`;
        rate = DEMO_EXCHANGE_RATES[key] || 1.0;
      }

      // Calculate amounts based on locked side
      let sendMinor: number;
      let receiveMinor: number;
      if (params.lockedSide === "SENDING") {
        sendMinor = Math.round(params.amount * Math.pow(10, srcDecimals));
        receiveMinor = srcCurrency === destCurrency
          ? sendMinor
          : Math.round(params.amount * rate * Math.pow(10, destDecimals));
      } else {
        receiveMinor = Math.round(params.amount * Math.pow(10, destDecimals));
        const inverseKey = `${destCurrency}:${srcCurrency}`;
        const inverseRate = DEMO_EXCHANGE_RATES[inverseKey] || (1 / rate);
        sendMinor = srcCurrency === destCurrency
          ? receiveMinor
          : Math.round(params.amount * inverseRate * Math.pow(10, srcDecimals));
      }

      // Check balance
      if (sourceAcct.balance.amount < sendMinor) {
        return {
          success: false,
          error: `Insufficient balance. Available: ${(sourceAcct.balance.amount / Math.pow(10, srcDecimals)).toFixed(srcDecimals === 8 ? 8 : 2)} ${srcCurrency}`,
        };
      }

      const fees = srcCurrency === destCurrency ? 0 : Math.round(sendMinor * 0.002);
      const now = new Date();
      const expiresAt = new Date(now.getTime() + 5 * 60 * 1000); // 5 min expiry

      const quote: Quote = {
        quoteId: `quote_demo_${Date.now()}`,
        status: "PENDING",
        source: {
          sourceType: "ACCOUNT",
          accountId: params.sourceAccountId,
        },
        destination: params.destinationUmaAddress
          ? {
              destinationType: "UMA_ADDRESS",
              umaAddress: params.destinationUmaAddress,
              currency: destCurrency,
            }
          : {
              destinationType: "ACCOUNT",
              accountId: params.destinationAccountId,
            },
        sendingCurrency: sourceAcct.balance.currency,
        receivingCurrency: destCurrObj,
        totalSendingAmount: sendMinor,
        totalReceivingAmount: receiveMinor,
        exchangeRate: rate,
        feesIncluded: fees,
        createdAt: now.toISOString(),
        expiresAt: expiresAt.toISOString(),
      };

      setDemo((prev) => ({
        ...prev,
        quotes: [quote, ...prev.quotes],
      }));

      return { success: true, quote };
    },
    [findInternalAccount],
  );

  // ─── POST /quotes/:id/execute simulation ────────────────────
  const demoExecuteQuote = useCallback(
    (quoteId: string) => {
      const quoteObj = demo.quotes.find((q) => q.quoteId === quoteId);
      if (!quoteObj) return { success: false, error: "Quote not found" };
      if (quoteObj.status !== "PENDING") return { success: false, error: `Quote is ${quoteObj.status}, not PENDING` };

      const sourceAcct = findInternalAccount(quoteObj.source.accountId || "");
      if (!sourceAcct) return { success: false, error: "Source account not found" };

      if (sourceAcct.balance.amount < quoteObj.totalSendingAmount) {
        return { success: false, error: "Insufficient balance" };
      }

      const now = new Date().toISOString();
      const txId = `tx_demo_${Date.now()}`;

      const tx: Transaction = {
        id: txId,
        customerId: sourceAcct.customerId,
        platformCustomerId: "",
        type: "OUTGOING",
        status: "COMPLETED",
        sentAmount: { amount: quoteObj.totalSendingAmount, currency: quoteObj.sendingCurrency },
        receivedAmount: { amount: quoteObj.totalReceivingAmount, currency: quoteObj.receivingCurrency },
        exchangeRate: quoteObj.exchangeRate,
        fees: quoteObj.feesIncluded,
        description: quoteObj.destination.umaAddress
          ? `Quote → ${quoteObj.destination.umaAddress}`
          : `Quote → External Account`,
        destination: quoteObj.destination.umaAddress || quoteObj.destination.accountId || "",
        source: quoteObj.source.accountId || "",
        createdAt: now,
        settledAt: now,
      };

      setDemo((prev) => {
        const updateAccount = (accounts: InternalAccount[], id: string, delta: number) =>
          accounts.map((a) =>
            a.id === id ? { ...a, balance: { ...a.balance, amount: a.balance.amount + delta }, updatedAt: now } : a,
          );

        let newIA = prev.internalAccounts;
        let newPlatIA = prev.platformInternalAccounts;

        // Debit source internal account
        if (sourceAcct.customerId === "PLATFORM") {
          newPlatIA = updateAccount(newPlatIA, quoteObj.source.accountId || "", -quoteObj.totalSendingAmount);
        } else {
          newIA = updateAccount(newIA, quoteObj.source.accountId || "", -quoteObj.totalSendingAmount);
        }

        // Mark quote as completed
        const newQuotes = prev.quotes.map((q) =>
          q.quoteId === quoteId ? { ...q, status: "COMPLETED" as const, transactionId: txId } : q,
        );

        return {
          ...prev,
          internalAccounts: newIA,
          platformInternalAccounts: newPlatIA,
          transactions: [tx, ...prev.transactions],
          quotes: newQuotes,
        };
      });

      return { success: true, transaction: tx };
    },
    [demo.quotes, findInternalAccount],
  );

  // ─── POST /transfer-out simulation (internal → external, same currency) ──
  const demoTransferOut = useCallback(
    (params: {
      sourceInternalAccountId: string;
      destExternalAccountId: string;
      amount: number;
      description?: string;
    }) => {
      const sourceAcct = findInternalAccount(params.sourceInternalAccountId);
      if (!sourceAcct) return { success: false, error: "Source internal account not found" };

      const destAcct = findExternalAccount(params.destExternalAccountId);
      if (!destAcct) return { success: false, error: "Destination external account not found" };

      const srcCode = sourceAcct.balance.currency.code;
      const destCode = destAcct.currency.code;
      if (srcCode !== destCode) {
        return { success: false, error: `Same-currency transfer requires matching currencies. Source: ${srcCode}, Destination: ${destCode}. Use a Quote for cross-currency transfers.` };
      }

      const decimals = sourceAcct.balance.currency.decimals;
      const minorAmount = Math.round(params.amount * Math.pow(10, decimals));

      if (sourceAcct.balance.amount < minorAmount) {
        return { success: false, error: `Insufficient balance. Available: ${(sourceAcct.balance.amount / Math.pow(10, decimals)).toFixed(decimals === 8 ? 8 : 2)} ${srcCode}` };
      }

      const now = new Date().toISOString();
      const tx: Transaction = {
        id: `tx_demo_${Date.now()}`,
        customerId: sourceAcct.customerId,
        platformCustomerId: "",
        type: "OUTGOING",
        status: "COMPLETED",
        sentAmount: { amount: minorAmount, currency: sourceAcct.balance.currency },
        receivedAmount: { amount: minorAmount, currency: destAcct.currency },
        exchangeRate: 1.0,
        fees: 0,
        description: params.description || `Transfer out to ${destAcct.accountLabel}`,
        destination: destAcct.accountLabel,
        source: params.sourceInternalAccountId,
        createdAt: now,
        settledAt: now,
      };

      setDemo((prev) => {
        const updateAccount = (accounts: InternalAccount[], id: string, delta: number) =>
          accounts.map((a) =>
            a.id === id ? { ...a, balance: { ...a.balance, amount: a.balance.amount + delta }, updatedAt: now } : a,
          );

        let newIA = prev.internalAccounts;
        let newPlatIA = prev.platformInternalAccounts;
        if (sourceAcct.customerId === "PLATFORM") {
          newPlatIA = updateAccount(newPlatIA, params.sourceInternalAccountId, -minorAmount);
        } else {
          newIA = updateAccount(newIA, params.sourceInternalAccountId, -minorAmount);
        }

        return {
          ...prev,
          internalAccounts: newIA,
          platformInternalAccounts: newPlatIA,
          transactions: [tx, ...prev.transactions],
        };
      });

      return { success: true, transaction: tx };
    },
    [findInternalAccount, findExternalAccount],
  );

  // ─── POST /transfer-in simulation (external → internal, same currency) ──
  const demoTransferIn = useCallback(
    (params: {
      sourceExternalAccountId: string;
      destInternalAccountId: string;
      amount: number;
      description?: string;
    }) => {
      const sourceAcct = findExternalAccount(params.sourceExternalAccountId);
      if (!sourceAcct) return { success: false, error: "Source external account not found" };

      const destAcct = findInternalAccount(params.destInternalAccountId);
      if (!destAcct) return { success: false, error: "Destination internal account not found" };

      const srcCode = sourceAcct.currency.code;
      const destCode = destAcct.balance.currency.code;
      if (srcCode !== destCode) {
        return { success: false, error: `Same-currency transfer requires matching currencies. Source: ${srcCode}, Destination: ${destCode}. Use a Quote for cross-currency transfers.` };
      }

      const decimals = destAcct.balance.currency.decimals;
      const minorAmount = Math.round(params.amount * Math.pow(10, decimals));

      const now = new Date().toISOString();
      const tx: Transaction = {
        id: `tx_demo_${Date.now()}`,
        customerId: destAcct.customerId,
        platformCustomerId: "",
        type: "INCOMING",
        status: "COMPLETED",
        sentAmount: { amount: minorAmount, currency: sourceAcct.currency },
        receivedAmount: { amount: minorAmount, currency: destAcct.balance.currency },
        exchangeRate: 1.0,
        fees: 0,
        description: params.description || `Transfer in from ${sourceAcct.accountLabel}`,
        destination: params.destInternalAccountId,
        source: sourceAcct.accountLabel,
        createdAt: now,
        settledAt: now,
      };

      setDemo((prev) => {
        const updateAccount = (accounts: InternalAccount[], id: string, delta: number) =>
          accounts.map((a) =>
            a.id === id ? { ...a, balance: { ...a.balance, amount: a.balance.amount + delta }, updatedAt: now } : a,
          );

        let newIA = prev.internalAccounts;
        let newPlatIA = prev.platformInternalAccounts;
        if (destAcct.customerId === "PLATFORM") {
          newPlatIA = updateAccount(newPlatIA, params.destInternalAccountId, minorAmount);
        } else {
          newIA = updateAccount(newIA, params.destInternalAccountId, minorAmount);
        }

        return {
          ...prev,
          internalAccounts: newIA,
          platformInternalAccounts: newPlatIA,
          transactions: [tx, ...prev.transactions],
        };
      });

      return { success: true, transaction: tx };
    },
    [findInternalAccount, findExternalAccount],
  );

  // ─── POST /customers/external-accounts simulation ──────────
  const demoCreateExternalAccount = useCallback(
    (params: { customerId: string; currency: string; accountType: string; accountLabel: string }) => {
      const currObj = CURRENCIES[params.currency] || { code: params.currency, name: params.currency, symbol: params.currency, decimals: 2 };
      const now = new Date().toISOString();

      const account: ExternalAccount = {
        id: `ea_demo_${Date.now()}`,
        customerId: params.customerId,
        accountType: params.accountType as any,
        accountLabel: params.accountLabel,
        currency: currObj,
        status: "ACTIVE",
        createdAt: now,
        updatedAt: now,
      };

      setDemo((prev) => ({
        ...prev,
        externalAccounts: [account, ...prev.externalAccounts],
      }));

      return { success: true, account };
    },
    [],
  );

  // ─── Internal account auto-provisioning simulation ────────
  const demoCreateInternalAccount = useCallback(
    (params: { customerId: string; currencyCode: string }) => {
      const currObj = CURRENCIES[params.currencyCode] || { code: params.currencyCode, name: params.currencyCode, symbol: params.currencyCode, decimals: 2 };
      const now = new Date().toISOString();

      // Check if account already exists for this customer+currency
      const existing = demo.internalAccounts.find(
        (a) => a.customerId === params.customerId && a.balance.currency.code === params.currencyCode,
      );
      if (existing) {
        return { success: false, error: `${params.currencyCode} internal account already exists for this customer` };
      }

      const account: InternalAccount = {
        id: `ia_demo_${Date.now()}`,
        customerId: params.customerId,
        balance: { amount: 0, currency: currObj },
        status: "ACTIVE",
        createdAt: now,
        updatedAt: now,
      };

      setDemo((prev) => ({
        ...prev,
        internalAccounts: [account, ...prev.internalAccounts],
      }));

      return { success: true, account };
    },
    [demo.internalAccounts],
  );

  return (
    <DemoContext.Provider
      value={{
        demo,
        toggleDemo,
        demoCreateQuote,
        demoExecuteQuote,
        demoTransferOut,
        demoTransferIn,
        demoCreateExternalAccount,
        demoCreateInternalAccount,
        getDemoInternalAccounts,
        getDemoExternalAccounts,
        getDemoCustomers,
        getDemoTransactions,
        getDemoQuotes,
      }}
    >
      {children}
    </DemoContext.Provider>
  );
}

export function useDemo() {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error("useDemo must be used inside DemoProvider");
  return ctx;
}
